import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CheckCircle2, XCircle } from "lucide-react";

/* =======================
   Styles
======================= */
import styles from "./APITrendChart.module.scss";

// Hardcoded JS values mapping to our SCSS variables
const COLOR_SUCCESS = "#10b981"; // $color-success
const COLOR_FAILED = "#ef4444"; // $color-danger
const AXIS_COLOR = "#94a3b8"; // $color-slate-400
const GRID_COLOR = "#e2e8f0"; // $color-slate-200
const TEXT_COLOR = "#64748b"; // $color-slate-500

/* =======================
   Custom Tooltip Component
======================= */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const successData = payload.find((p) => p.dataKey === "success");
    const failedData = payload.find((p) => p.dataKey === "failed");

    return (
      <div className={styles["admin-atc__tooltip"]}>
        <p className={styles["admin-atc__tooltip-label"]}>{label}</p>

        {successData && (
          <div className={styles["admin-atc__tooltip-row"]}>
            <div
              className={`${styles["admin-atc__tooltip-item"]} ${styles["admin-atc__tooltip-item--success"]}`}
            >
              <CheckCircle2 size={16} />
              <span>Success</span>
            </div>
            <span className={styles["admin-atc__tooltip-value"]}>
              {successData.value.toLocaleString()}
            </span>
          </div>
        )}

        {failedData && (
          <div className={styles["admin-atc__tooltip-row"]}>
            <div
              className={`${styles["admin-atc__tooltip-item"]} ${styles["admin-atc__tooltip-item--failed"]}`}
            >
              <XCircle size={16} />
              <span>Failed</span>
            </div>
            <span className={styles["admin-atc__tooltip-value"]}>
              {failedData.value.toLocaleString()}
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

/* =======================
   Custom Legend Component
======================= */
const CustomLegend = () => {
  return (
    <div className={styles["admin-atc__legend"]}>
      <div
        className={`${styles["admin-atc__legend-pill"]} ${styles["admin-atc__legend-pill--success"]}`}
      >
        <CheckCircle2 size={16} />
        <span className={styles["admin-atc__legend-text"]}>Success</span>
      </div>

      <div
        className={`${styles["admin-atc__legend-pill"]} ${styles["admin-atc__legend-pill--failed"]}`}
      >
        <XCircle size={16} />
        <span className={styles["admin-atc__legend-text"]}>Failed</span>
      </div>
    </div>
  );
};

const APITrendChart = ({ data = [] }) => {
  return (
    <div className={styles["admin-atc__card"]}>
      <h4 className={styles["admin-atc__title"]}>API Calls Trend</h4>

      <div className={styles["admin-atc__chart-wrapper"]}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLOR_SUCCESS} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLOR_SUCCESS} stopOpacity={0} />
              </linearGradient>

              <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLOR_FAILED} stopOpacity={0.3} />
                <stop offset="95%" stopColor={COLOR_FAILED} stopOpacity={0} />
              </linearGradient>
            </defs>

            {/* Softer grid lines */}
            <CartesianGrid
              strokeDasharray="4 4"
              stroke={GRID_COLOR}
              vertical={false}
            />

            <XAxis
              dataKey="day"
              tick={{ fontSize: 12, fill: TEXT_COLOR, fontWeight: 500 }}
              axisLine={{ stroke: AXIS_COLOR, strokeWidth: 1 }}
              tickLine={false}
              dy={10}
            />

            <YAxis
              tick={{ fontSize: 12, fill: TEXT_COLOR, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              dx={-10}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: GRID_COLOR, strokeWidth: 2 }}
            />

            <Legend verticalAlign="bottom" content={<CustomLegend />} />

            <Area
              type="monotone"
              dataKey="success"
              stroke={COLOR_SUCCESS}
              strokeWidth={3}
              fill="url(#successGradient)"
              name="Success"
              activeDot={{
                r: 6,
                fill: COLOR_SUCCESS,
                stroke: "#fff",
                strokeWidth: 2,
              }}
            />

            <Area
              type="monotone"
              dataKey="failed"
              stroke={COLOR_FAILED}
              strokeWidth={3}
              fill="url(#failedGradient)"
              name="Failed"
              activeDot={{
                r: 6,
                fill: COLOR_FAILED,
                stroke: "#fff",
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default APITrendChart;
